import { z } from "zod";
import { ADD_GLOBAL_MEMBERS_MAX, createGroupMemberSeatsRequestSchema, groupRoleSchema } from "./groups.js";
import { ADMIN_GROUP_ROLE_REASON_MAX_LENGTH } from "./admin-group-roles.js";

/**
 * Shared DTO — Admin "Học sinh & phụ huynh / Thành viên" (US-AE4.F6-0859):
 *  - `@UC9945` bảng suất: tìm (tên suất + MỌI người đại diện, không dấu, ở server) · lọc · phân trang con trỏ
 *  - `@UC9946` SUPER_ADMIN gắn / gỡ người đại diện — đi CÙNG `MemberAccountMappingService` của app
 *  - `@UC9947` SUPER_ADMIN sửa ngày sinh của suất
 *
 * Request `.strict()`; actor lấy từ phiên admin, không bao giờ từ body. Lý do dùng LẠI hằng của 0856/0857
 * (`ADMIN_GROUP_ROLE_REASON_MAX_LENGTH`) — không chép số. 0 bút toán.
 *
 * Quy ước che theo vai của lane admin: field bị ẩn = `null` (KHÔNG `0`, KHÔNG `'•••'`) — cùng luật với
 * `adminGroupMemberItemSchema` (`admin-group-directory.ts`).
 */

const reasonSchema = z.string().trim().min(1).max(ADMIN_GROUP_ROLE_REASON_MAX_LENGTH);
const supportTicketIdSchema = z.string().uuid().optional();

/** Trần `q` — tên người Việt dài nhất vẫn dư; chặn input phình to tại biên. */
export const ADMIN_GROUP_MEMBERS_MAX_QUERY_LENGTH = 100;
/** Trần page-size (mặc định 20 do backend áp khi vắng — `normalizeLimit`). */
export const ADMIN_GROUP_MEMBERS_MAX_LIMIT = 50;
/** Cursor opaque luôn ngắn — đối xứng `MAX_CURSOR_LENGTH` của `common/pagination/cursor.ts`. */
const MAX_CURSOR_LENGTH = 512;

/**
 * Bộ lọc bảng suất (QĐ-11: dropdown, không chip đếm số):
 *  - `all` — mọi suất `active` + `unclaimed`
 *  - `no_representative` — suất KHÔNG có ai đứng tên còn sống (chỉ có nghĩa ở nhóm phụ huynh; nhóm thường ⇒ như `all`)
 *  - `managers` — suất mang vai quản lý (khác `member`)
 */
export const ADMIN_GROUP_MEMBERS_FILTERS = ["all", "no_representative", "managers"] as const;
export const adminGroupMembersFilterSchema = z.enum(ADMIN_GROUP_MEMBERS_FILTERS);
export type AdminGroupMembersFilter = z.infer<typeof adminGroupMembersFilterSchema>;

/** Query `GET /v1/admin/groups/:id/members`. `q` < 2 ký tự (sau trim) ⇒ server KHÔNG lọc theo tên. */
export const adminGroupMembersQuerySchema = z
  .object({
    q: z.string().trim().max(ADMIN_GROUP_MEMBERS_MAX_QUERY_LENGTH).optional(),
    filter: adminGroupMembersFilterSchema.default("all"),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_GROUP_MEMBERS_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminGroupMembersQuery = z.infer<typeof adminGroupMembersQuerySchema>;

/** Ngày trần `YYYY-MM-DD` CÓ THẬT trên lịch (2018-02-30 ⇒ fail) — cùng dạng dây với `newMember.birthDate`. */
const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Ngày phải theo định dạng YYYY-MM-DD.")
  .refine((s) => {
    const d = new Date(`${s}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, "Ngày không có thật trên lịch.");

/**
 * Một người đại diện của suất (liên kết `membership_accounts` CÒN SỐNG).
 * 🔴 CHỈ `primary_contact` | `secondary_contact`: `self` (tự đứng tên) không phải "người đại diện", và vai
 * `viewer` ngoài phạm vi (QĐ-8) — hai giá trị đó KHÔNG biểu diễn được ở đây.
 * `name` = tên tài khoản · `groupDisplayName` = tên trong nhóm (vd "Mẹ bé An") — cả hai là PII, `null` = che
 * theo vai (cùng luật `displayName`). KHÔNG email / điện thoại (`.strict()`).
 */
export const adminSeatRepresentativeSchema = z
  .object({
    userId: z.string(),
    name: z.string().nullable(),
    groupDisplayName: z.string().nullable(),
    linkRole: z.enum(["primary_contact", "secondary_contact"]),
    linkedAt: z.string(),
  })
  .strict();
export type AdminSeatRepresentative = z.infer<typeof adminSeatRepresentativeSchema>;

/**
 * Một dòng bảng suất. `status` CHỈ `active` | `unclaimed` (QĐ-4 — suất chờ duyệt ở khối Hàng chờ; suất đã
 * rời / bị gỡ / đã nhận không thuộc bảng này ⇒ không biểu diễn được).
 * `totalContributedMinor`: chuỗi số nguyên minor (B1) hoặc `null` = che theo vai — `"0"` và `null` khác nhau.
 */
export const adminGroupSeatItemSchema = z
  .object({
    groupMemberId: z.string(),
    userId: z.string().nullable(),
    displayName: z.string().nullable(),
    birthDate: calendarDateSchema.nullable(),
    role: groupRoleSchema,
    status: z.enum(["active", "unclaimed"]),
    totalContributedMinor: z
      .string()
      .regex(/^-?\d+$/, "totalContributedMinor phải là số nguyên minor units (không float)")
      .nullable(),
    roleEligibility: z.enum(["eligible", "no_primary_account", "account_inactive"]),
    representatives: z.array(adminSeatRepresentativeSchema),
  })
  .strict();
export type AdminGroupSeatItem = z.infer<typeof adminGroupSeatItemSchema>;

export const adminGroupSeatListResponseSchema = z.object({
  data: z.array(adminGroupSeatItemSchema),
  pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }),
});
export type AdminGroupSeatListResponse = z.infer<typeof adminGroupSeatListResponseSchema>;

/** Query `GET /v1/admin/groups/:id/representative-candidates`. Vắng / < 2 ký tự ⇒ chỉ người trong nhóm. */
export const adminRepresentativeCandidatesQuerySchema = z
  .object({ q: z.string().trim().max(ADMIN_GROUP_MEMBERS_MAX_QUERY_LENGTH).optional() })
  .strict();
export type AdminRepresentativeCandidatesQuery = z.infer<typeof adminRepresentativeCandidatesQuerySchema>;

/**
 * Một ứng viên người đại diện. `outsideGroup` = chưa đứng tên suất nào của nhóm (bước xác nhận cảnh báo
 * "có thể phải đóng góp theo cấu hình quỹ hiện tại" — QĐ-5). `representsSeats` = các suất họ đang đại diện
 * trong nhóm này. 🔴 KHÔNG email / điện thoại thô (`.strict()` chặn rò). `handle` = mã định danh công khai.
 */
export const adminRepresentativeCandidateSchema = z
  .object({
    userId: z.string(),
    name: z.string().nullable(),
    handle: z.string().nullable(),
    outsideGroup: z.boolean(),
    representsSeats: z.array(z.object({ groupMemberId: z.string(), displayName: z.string().nullable() }).strict()),
  })
  .strict();
export type AdminRepresentativeCandidate = z.infer<typeof adminRepresentativeCandidateSchema>;

export const adminRepresentativeCandidatesResponseSchema = z.object({
  items: z.array(adminRepresentativeCandidateSchema),
});
export type AdminRepresentativeCandidatesResponse = z.infer<typeof adminRepresentativeCandidatesResponseSchema>;

/**
 * Body `POST /v1/admin/groups/:id/members/:memberId/representatives`. KHÔNG có `linkRole`: máy xếp chính/phụ
 * y như app (chọn tay + `viewer` ngoài phạm vi — story §4).
 */
export const adminAddRepresentativeBodySchema = z
  .object({ userId: z.string().uuid(), reason: reasonSchema, supportTicketId: supportTicketIdSchema })
  .strict();
export type AdminAddRepresentativeBody = z.infer<typeof adminAddRepresentativeBodySchema>;

export const adminAddRepresentativeResponseSchema = z.object({
  groupMemberId: z.string(),
  userId: z.string(),
  linkRole: z.enum(["primary_contact", "secondary_contact"]),
  /** Trạng thái suất SAU khi gắn — suất `unclaimed` nhận người đầu tiên ⇒ `active`. */
  seatStatus: z.string(),
});
export type AdminAddRepresentativeResponse = z.infer<typeof adminAddRepresentativeResponseSchema>;

/** Body `POST /v1/admin/groups/:id/members/:memberId/representatives/:userId/remove`. */
export const adminRemoveRepresentativeBodySchema = z
  .object({ reason: reasonSchema, supportTicketId: supportTicketIdSchema })
  .strict();
export type AdminRemoveRepresentativeBody = z.infer<typeof adminRemoveRepresentativeBodySchema>;

export const adminRemoveRepresentativeResponseSchema = z.object({
  groupMemberId: z.string(),
  userId: z.string(),
  /** Suất không còn ai đứng tên sau khi gỡ. */
  becameGhost: z.boolean(),
});
export type AdminRemoveRepresentativeResponse = z.infer<typeof adminRemoveRepresentativeResponseSchema>;

/**
 * Body `PATCH /v1/admin/groups/:id/members/:memberId/birth-date` (QĐ-13). `birthDate` BẮT BUỘC có mặt:
 * `null` = xoá trống; vắng khoá ⇒ 400 (không để "quên gửi" bị hiểu thành "xoá"). Không ở tương lai — kiểm ở
 * server theo ngày VN (`ADMIN_GROUP_MEMBERS_CODE.BIRTH_DATE_IN_FUTURE`).
 */
export const adminEditMemberBirthDateBodySchema = z
  .object({ birthDate: calendarDateSchema.nullable(), reason: reasonSchema, supportTicketId: supportTicketIdSchema })
  .strict();
export type AdminEditMemberBirthDateBody = z.infer<typeof adminEditMemberBirthDateBodySchema>;

export const adminEditMemberBirthDateResponseSchema = z.object({
  groupMemberId: z.string(),
  birthDate: z.string().nullable(),
});
export type AdminEditMemberBirthDateResponse = z.infer<typeof adminEditMemberBirthDateResponseSchema>;

/**
 * US-AE4.F7-1015 (@UC9946 · QĐ người dùng 12/10/2026) — body `POST /v1/admin/groups/:id/members/add`: nhân viên THÊM thành viên,
 * đi ĐÚNG lõi của app (không cơ chế thứ hai):
 *  - `seats` — tạo SUẤT chưa gắn tài khoản (`MembersService.createSeatsInTx`): nhóm phụ huynh = bé (phụ huynh gắn sau bằng nút
 *    *Gắn phụ huynh*); nhóm thường = giữ chỗ người chưa có app. Danh sách + trần lấy NGUYÊN từ contract của app.
 *  - `accounts` — kéo tài khoản có sẵn vào nhóm THƯỜNG (`MembersService.addFromQueueInTx`, nhánh `newUserIds`; trần
 *    `ADD_GLOBAL_MEMBERS_MAX`). Nhóm phụ huynh ⇒ 422 `GLOBAL_ADD_STANDARD_ONLY` của app.
 */
export const adminAddGroupMembersBodySchema = z
  .object({
    kind: z.enum(["seats", "accounts"]),
    seats: createGroupMemberSeatsRequestSchema.shape.members.optional(),
    userIds: z.array(z.string().uuid()).min(1).max(ADD_GLOBAL_MEMBERS_MAX).optional(),
    reason: reasonSchema,
    supportTicketId: supportTicketIdSchema,
  })
  .strict()
  // Một object (không union — `createZodDto` cần kiểu object) nhưng trạng thái sai vẫn KHÔNG biểu diễn được: `kind` nào mang ĐÚNG
  // danh sách của nó, không mang danh sách kia.
  .superRefine((v, ctx) => {
    const own = v.kind === "seats" ? "seats" : "userIds";
    const other = v.kind === "seats" ? "userIds" : "seats";
    if (v[own] === undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [own], message: "required" });
    if (v[other] !== undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [other], message: "not_allowed" });
  });
export type AdminAddGroupMembersBody = z.infer<typeof adminAddGroupMembersBodySchema>;

export const adminAddGroupMembersResponseSchema = z
  .object({
    kind: z.enum(["seats", "accounts"]),
    /** Số người / suất THẬT được thêm (lõi app bỏ qua người đã ở trong nhóm). */
    added: z.number().int().nonnegative(),
  })
  .strict();
export type AdminAddGroupMembersResponse = z.infer<typeof adminAddGroupMembersResponseSchema>;

/**
 * US-AE4.F7-1016 (@UC9946 @UC0029 · QĐ người dùng 13/10/2026) — XOÁ thành viên khỏi nhóm, đi ĐÚNG lõi app (`MembersService.removeMember`).
 * Lý do nút "Xoá khỏi nhóm" bị khoá — MÁY CHỦ tính (`GET …/members/:memberId/remove-check`), cùng vị-từ với chốt thật:
 *  - `removal_disabled` — van khẩn cấp `GROUP_MEMBER_REMOVAL_ENABLED=false` của lõi app (409 `GROUP_MEMBER_REMOVAL_DISABLED`) — tắt toàn hệ thống;
 *  - `group_suspended` · `group_archived` — nhóm đình chỉ / lưu trữ (QĐ 13/10: lưu trữ không cho xoá);
 *  - `owner` — chủ nhóm (chuyển chủ trước);
 *  - `unpaid_dues` — còn khoản chưa xong (chưa đóng · đóng dở · quá hạn · chờ duyệt) — đóng hoặc miễn ở màn Đợt thu trước;
 *  - `open_debt` — còn nợ khác trong nhóm (nợ người↔người · nợ quỹ · khoản vay) — không có lối ra ở admin.
 */
export const ADMIN_REMOVE_MEMBER_LOCKS = ["removal_disabled", "group_suspended", "group_archived", "owner", "unpaid_dues", "open_debt"] as const;
export type AdminRemoveMemberLock = (typeof ADMIN_REMOVE_MEMBER_LOCKS)[number];

export const adminRemoveMemberCheckResponseSchema = z
  .object({ lock: z.enum(ADMIN_REMOVE_MEMBER_LOCKS).nullable() })
  .strict();
export type AdminRemoveMemberCheckResponse = z.infer<typeof adminRemoveMemberCheckResponseSchema>;

/** Body `POST /v1/admin/groups/:id/members/:memberId/remove` — lý do bắt buộc. Không có cờ "xoá và miễn" (QĐ 13/10: như bình thường). */
export const adminRemoveGroupMemberBodySchema = z
  .object({ reason: reasonSchema, supportTicketId: supportTicketIdSchema })
  .strict();
export type AdminRemoveGroupMemberBody = z.infer<typeof adminRemoveGroupMemberBodySchema>;

export const adminRemoveGroupMemberResponseSchema = z
  .object({ groupMemberId: z.string(), status: z.literal("removed") })
  .strict();
export type AdminRemoveGroupMemberResponse = z.infer<typeof adminRemoveGroupMemberResponseSchema>;

/**
 * Mã lỗi ổn định (problem+json `code`) RIÊNG của đường admin này. Lỗi của service app (gắn/gỡ) giữ NGUYÊN mã
 * của app — `ACCOUNT_MAPPING_CODE` (`GROUP_ACCOUNT_ALREADY_MAPPED` · `GROUP_ACCOUNT_LAST_REPRESENTATIVE` ·
 * `GROUP_ACCOUNT_NOT_FOUND` · `GROUP_ACCOUNT_SEAT_NOT_MAPPABLE` …).
 */
export const ADMIN_GROUP_MEMBERS_CODE = {
  /** Suất đã rời / bị gỡ / đã được nhận vào suất khác — không sửa ngày sinh. */
  SEAT_NOT_EDITABLE: "ADMIN_GROUP_MEMBERS_SEAT_NOT_EDITABLE",
  /** Ngày sinh sau hôm nay (theo ngày Việt Nam). */
  BIRTH_DATE_IN_FUTURE: "ADMIN_GROUP_MEMBERS_BIRTH_DATE_IN_FUTURE",
  /**
   * Gắn người đại diện là tài khoản không còn hoạt động (khoá / ẩn danh / đã xoá) — cùng luật "đang hoạt động" của
   * danh sách ứng viên (`status = active` VÀ `deletedAt IS NULL`). Chỉ chặn ở đường ADMIN; đường app giữ nguyên.
   */
  ACCOUNT_INACTIVE: "ADMIN_GROUP_MEMBERS_ACCOUNT_INACTIVE",
} as const;
